import { samplingRate } from '../helper/index.ts';

import { version } from '../../version.ts';

class VerifyReporter {
  private static instance: VerifyReporter;
  private customMonitorUrl: string = '';
  private needReport: boolean = true;

  private commonParams = {};

  public static getInstance() {
    if (!VerifyReporter.instance) {
      VerifyReporter.instance = new VerifyReporter();
    }
    return VerifyReporter.instance;
  }

  public async init(baseURL: any, rate: number = 10000) {
    try {
      this.needReport = samplingRate(rate);
      let server_type = 'central';
      const gbCommonInfo = window?.gbCommonInfo;
      if (this.isLocalDevelopmentUrl(window.location.href)) {
        server_type = 'debug';
      } else if (typeof baseURL === 'string' && baseURL.length > 0) {
        if (baseURL.includes('us')) {
          server_type = 'usa';
        } else if (baseURL.includes('eur')) {
          server_type = 'eur';
        } else {
          //站外 默认中心
          server_type = 'central';
        }
      } else {
        server_type = gbCommonInfo?.SERVER_TYPE ?? 'central';
      }
      this.customMonitorUrl = this.getMonitorUrl(server_type);

      const app_name = gbCommonInfo?.WEB_CLIENT ?? 'other';
      const site_uid = gbCommonInfo?.SiteUID ?? 'www';
      const money_type = gbCommonInfo?.currency ?? 'USD';
      const isInApp =
        '/h5' == gbCommonInfo?.langPath ||
        /SheinApp/.test(window.navigator.userAgent);
      const platform = isInApp
        ? site_uid?.indexOf('ios') > -1
          ? 'ios'
          : 'android'
        : (gbCommonInfo?.PLATFORM ?? 'pc');

      const sdkVerion = version;
      this.commonParams = {
        app_name: app_name,
        device_type: platform,
        sub_site: site_uid,
        money_type: money_type,
        language: navigator.language,
        environment: server_type === 'debug' ? 'debug' : 'production',
        level: 'INFO',
        sdk_version: sdkVerion,
        session_id: '',
        member_id: '',
        params: {
          browser_name: this.getBrowserName(),
          device_id: '',
        },
      };
    } catch (e) {
      console.error(e);
    }
  }

  public report(params) {
    try {
      const gbCommonInfo = window?.gbCommonInfo;
      // error类型的日志不采样
      params = params
        .filter((item) => {
          return item.verify_log_type === 'verify_error' || this.needReport;
        })
        .map((item) => {
          item['env'] = gbCommonInfo?.NODE_SERVER_ENV;
          return item;
        });

      if (params.length === 0) {
        return;
      }

      this.reportMonitor(params);
    } catch (e) {
      console.error(e);
    }
  }

  private getMonitorUrl(server_type) {
    if (server_type === 'central') {
      return 'https://www.srmdata.com/app-track/metric-info';
    } else if (server_type === 'usa') {
      return 'https://www.srmdata-us.com/app-track/metric-info';
    } else if (server_type === 'eur') {
      return 'https://www.srmdata-eur.com/app-track/metric-info';
    } else {
      return 'https://srmdata.abc-test.sheincorp.cn/app-track/metric-info';
    }
  }

  private async reportMonitor(params) {
    try {
      const device_type = this.commonParams['device_type'] ?? '';
      const env = this.commonParams['environment'] ?? '';
      if (!this.commonParams['params']) {
        this.commonParams['params'] = {};
      }
      if (!this.commonParams['params']['device_id']) {
        this.commonParams['params']['device_id'] = window['__SmDeviceId'] ?? '';
      }

      const reportList = params.map((item) => {
        const {
          validate_type1,
          validate_type2,
          validate_scene,
          result = '',
          status,
        } = item;
        const tags = {
          validate_type1,
          validate_type2,
          validate_scene,
          result,
          env,
          device_type,
          status,
          browser_name: this.getBrowserName(),
          app_name: this.commonParams['app_name'],
          sdk_version: this.commonParams['sdk_version'],
        };
        const extParams = {
          timestamp: Date.now(),
          local_time: new Date().toLocaleString(),
        };
        const is_log = [
          'verify_count',
          'verify_success',
          'verify_error',
          'verify_cancel',
          'verify_after_token_count',
          'verify_block_first_close_count',
        ].includes(status);
        const metric_name = is_log ? 'verify_web_log' : 'verify_web_metric';
        const messageObj = {
          metric_name,
          ...item,
          referer: window.location.href,
        };
        const message = JSON.stringify(messageObj);
        return !is_log
          ? {
              metric_name,
              tags,
              message,
              value: item.value ?? 0,
              ...this.commonParams,
              ...extParams,
            }
          : {
              metric_name,
              tags,
              message,
              value: 1,
              ...this.commonParams,
              ...extParams,
            };
      });

      const response = await fetch(this.customMonitorUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'DS-ACCESS-TOKEN': '4bc3a2dd3549401b817438eede7e78da',
          'DS-ACCESS-SITE': 'shein',
          'X-Report-Source': 'shein_w',
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify({ data: reportList }),
      });
      if (response.ok) {
        return response;
      } else {
        console.error('HTTP请求失败: ' + response.status);
      }
    } catch (e) {
      console.error(e);
    }
  }

  private getBrowserName(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Firefox')) {
      return 'Mozilla Firefox';
    } else if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) {
      // 注意：Edge浏览器的userAgent也包含"Chrome"
      return 'Google Chrome';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      // 注意：Chrome浏览器的userAgent也包含"Safari"
      return 'Safari';
    } else if (userAgent.includes('Edge')) {
      return 'Microsoft Edge';
    } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
      return 'Microsoft Internet Explorer';
    }
    return 'Unknown';
  }

  private isLocalDevelopmentUrl(url: string) {
    // 用于匹配本地开发环境URL的正则表达式
    const localDevelopmentRegex =
      /^(http:\/\/|https:\/\/)?(localhost|127\.0\.0\.1|::1|(10\.\d{1,3}\.\d{1,3}\.\d{1,3})|(172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3})|(192\.168\.\d{1,3}\.\d{1,3}))(:\d+)?(\/.*)?$/;
    // 解析URL以获取主机名
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    // 检查URL是否匹配本地开发环境的正则表达式
    // 并且主机名包含"test"
    return localDevelopmentRegex.test(url) || hostname.includes('test');
  }
}

export default VerifyReporter.getInstance();
